import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../stores/useAppStore';
import { Header, Footer, Toast } from '../../components';
import type { ToastMessage } from '../../components';
import { orderApi, publicApi } from '../../services/api';
import type { PaymentMethod } from '../../services/api';
import { useAuthStore } from '../../stores/useAuthStore';
import WalletSelector from '../../components/WalletSelector/WalletSelector';
import { QRCodeSVG } from 'qrcode.react';
import { buildAssets, looksLikeAddress, validateAssetAddress } from '../../utils/assets';
import { cardBank, cardDigits, cardScheme, formatCard, luhnOk } from '../../utils/card';

const DEFAULT_FAQ_ITEMS = [
  { q: 'Какие способы оплаты доступны?', a: 'Оплата через СБП (Система Быстрых Платежей) — быстро и без комиссий.' },
  { q: 'Как быстро приходит USDT?', a: 'USDT отправляется в течение 5-15 минут после подтверждения оплаты.' },
  { q: 'Какая минимальная сумма?', a: 'Минимальная сумма покупки зависит от текущих настроек обменника.' },
  { q: 'Осуществляете ли вы возвраты?', a: 'Да, в течение 24 часов с момента оплаты, если USDT ещё не были отправлены.' },
  { q: 'Остались вопросы?', a: 'Напишите нам в Telegram — мы на связи 24/7.' },
] as const;

export function HomePage() {
  const navigate = useNavigate();
  const {
    setUsdtAmount,
    walletAddress, setWalletAddress,
    settings, prices, paymentMethods, faqs, setPrices, sell, setSell
  } = useAppStore();

  const faqItems = faqs.length > 0 ? faqs : DEFAULT_FAQ_ITEMS;

  const [asset, setAsset] = useState('');
  const [isAssetOpen, setIsAssetOpen] = useState(false);
  const [usdtInput, setUsdtInput] = useState('');
  const [rubInput, setRubInput] = useState('');
  const [inputMode, setInputMode] = useState<'usdt' | 'rub'>('usdt');
  const [wallet, setWallet] = useState(walletAddress);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [price, setPrice] = useState<{ base_price: number; discount: number; service_commission: number; transfer_fee: number; final_price: number } | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // Продажа: клиент отдаёт USDT, получает рубли на карту
  const [direction, setDirection] = useState<'buy' | 'sell'>('buy');
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [sellUsdt, setSellUsdt] = useState('');
  const [sellRubInput, setSellRubInput] = useState('');
  const [sellInputMode, setSellInputMode] = useState<'usdt' | 'rub'>('usdt');
  const [card, setCard] = useState('');
  const [cardTouched, setCardTouched] = useState(false);
  const sendRef = useRef<HTMLDivElement | null>(null);
  const [rateTick, setRateTick] = useState(0);
  const assetRef = useRef<HTMLDivElement | null>(null);
  const methodRef = useRef<HTMLDivElement | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const authUser = useAuthStore((s) => s.user);
  const authToken = useAuthStore((s) => s.token);

  const showToast = useCallback((type: ToastMessage['type'], message: string, duration?: number) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message, duration }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Auto-select first payment method if only one
  useEffect(() => {
    if (paymentMethods.length === 1 && !paymentMethod) {
      setPaymentMethod(paymentMethods[0].code);
    }
  }, [paymentMethods, paymentMethod]);

  // Список монет задаётся в админке (price_configs); по умолчанию — первая активная.
  // Мемоизация обязательна: новые объекты на каждом рендере пересоздают
  // колбэки расчёта ниже, а их эффекты зовут setPrice — рендер зацикливается.
  const assets = useMemo(() => buildAssets(prices), [prices]);
  // По умолчанию USDT, если он включён; иначе первая монета из списка админки.
  const selectedAsset =
    assets.find((a) => a.code === asset) ||
    assets.find((a) => a.code === 'usdt') ||
    assets[0];
  const assetCode = selectedAsset?.code || 'usdt';
  const ticker = selectedAsset?.ticker || 'USDT';
  const network = selectedAsset?.network || 'TRC-20';
  const assetIcon = selectedAsset?.icon_url || settings['icon_usdt'] || '/usdt.svg';
  const assetDecimals = selectedAsset?.decimals ?? 2;
  // «USDT (Tether)» из админки показываем подписью, тикер держим коротким
  const assetName = selectedAsset && selectedAsset.label !== selectedAsset.ticker
    ? selectedAsset.label
    : '';

  const usdtConfigGlobal = selectedAsset;
  const commissionPctGlobal = parseFloat(settings['service_commission_percent'] || '10');
  // Комиссия сети у каждой монеты своя; у USDT сохранился старый глобальный сеттинг.
  const transferFeeUsdtGlobal =
    selectedAsset?.transfer_fee ||
    (assetCode === 'usdt' ? parseFloat(settings['usdt_transfer_fee_usdt'] || '2') : 0);
  const rateGlobal = usdtConfigGlobal?.base_price ?? 97;

  const commissionsEnabled = (settings['payment_commission_enabled'] ?? 'true')
    .toString().toLowerCase().trim() !== 'false';

  const selectedMethodObj = paymentMethods.find(m => m.code === paymentMethod);

  const computePriceFromUsdt = useCallback((numUsdt: number) => {
    const basePrice = numUsdt * rateGlobal;
    let discount = 0;
    if (
      usdtConfigGlobal?.discount_threshold &&
      numUsdt >= usdtConfigGlobal.discount_threshold &&
      usdtConfigGlobal.discount_percent
    ) {
      discount = basePrice * (usdtConfigGlobal.discount_percent / 100);
    }
    const subtotal = basePrice - discount;
    const serviceCommission = commissionsEnabled ? subtotal * (commissionPctGlobal / 100) : 0;
    const transferFee = commissionsEnabled ? transferFeeUsdtGlobal * rateGlobal : 0;
    let final_price = subtotal + serviceCommission + transferFee;
    if (selectedMethodObj) {
      if (selectedMethodObj.commission_percent) {
        final_price += final_price * (selectedMethodObj.commission_percent / 100);
      }
      if (selectedMethodObj.commission_fixed) {
        final_price += selectedMethodObj.commission_fixed;
      }
    }
    return {
      base_price: basePrice,
      discount,
      service_commission: serviceCommission,
      transfer_fee: transferFee,
      final_price: Math.round(final_price),
    };
  }, [rateGlobal, commissionPctGlobal, transferFeeUsdtGlobal, usdtConfigGlobal, commissionsEnabled, selectedMethodObj]);

  // Inverse: given target final RUB price, find USDT amount
  const usdtFromRub = useCallback((rubFinal: number): number => {
    if (rubFinal <= 0 || rateGlobal <= 0) return 0;
    // Strip payment-method commission first — backend adds it on top.
    let target = rubFinal;
    if (selectedMethodObj) {
      const m = (selectedMethodObj.commission_percent || 0) / 100;
      const f = selectedMethodObj.commission_fixed || 0;
      target = (rubFinal - f) / (1 + m);
    }
    const c = commissionsEnabled ? commissionPctGlobal / 100 : 0;
    const transferFeeRub = commissionsEnabled ? transferFeeUsdtGlobal * rateGlobal : 0;
    let usdt = (target - transferFeeRub) / (rateGlobal * (1 + c));
    if (
      usdtConfigGlobal?.discount_threshold &&
      usdtConfigGlobal.discount_percent &&
      usdt >= usdtConfigGlobal.discount_threshold
    ) {
      const d = usdtConfigGlobal.discount_percent / 100;
      const candidate = (target - transferFeeRub) / (rateGlobal * (1 - d) * (1 + c));
      if (candidate >= usdtConfigGlobal.discount_threshold) {
        usdt = candidate;
      }
    }
    return Math.max(usdt, 0);
  }, [rateGlobal, commissionPctGlobal, transferFeeUsdtGlobal, usdtConfigGlobal, commissionsEnabled, selectedMethodObj]);

  // In RUB mode, re-derive USDT when commissions/method change
  useEffect(() => {
    if (inputMode !== 'rub') return;
    const numRub = parseFloat(rubInput);
    if (numRub > 0) {
      const usdt = usdtFromRub(numRub);
      setUsdtInput(usdt > 0 ? usdt.toFixed(4) : '');
    }
  }, [usdtFromRub, inputMode, rubInput]);

  // Calculate price + keep the inactive field in sync
  useEffect(() => {
    const numUsdt = parseFloat(usdtInput);
    if (numUsdt > 0) {
      const next = computePriceFromUsdt(numUsdt);
      setPrice(next);
      if (inputMode === 'usdt') {
        setRubInput(next.final_price.toFixed(2));
      }
    } else {
      setPrice(null);
      if (inputMode === 'usdt') setRubInput('');
    }
  }, [usdtInput, computePriceFromUsdt, inputMode]);

  const handlePay = async () => {
    if (!paymentMethod || isLoading) return;

    if (!authToken || !authUser) {
      showToast('info', 'Войдите в аккаунт, чтобы совершить покупку');
      navigate('/login', { state: { from: '/' } });
      return;
    }
    if (!authUser.is_fully_verified) {
      showToast('error', 'Подтвердите телефон в личном кабинете');
      navigate('/account');
      return;
    }
    if (!authUser.full_name || authUser.full_name.trim().length < 2) {
      showToast('error', 'Укажите ФИО в разделе верификации');
      navigate('/account/verification');
      return;
    }

    const walletValidation = await validateAssetAddress(wallet, network);
    if (!walletValidation.valid) {
      showToast('error', walletValidation.reason);
      return;
    }

    const usdtNum = parseFloat(usdtInput) || 0;
    const minUsdt = selectedAsset?.min_amount || 1;
    const maxUsdt = selectedAsset?.max_amount || 10000;

    if (usdtNum < minUsdt) {
      showToast('error', `Минимальное количество ${ticker}: ${minUsdt}`);
      return;
    }
    if (usdtNum > maxUsdt) {
      showToast('error', `Максимальное количество ${ticker}: ${maxUsdt}`);
      return;
    }

    // Check payment method min amount
    const selectedMethod = paymentMethods.find(m => m.code === paymentMethod);
    if (selectedMethod?.min_amount && price?.final_price && price.final_price < selectedMethod.min_amount) {
      showToast('error', `Минимальная сумма для ${selectedMethod.name}: ${selectedMethod.min_amount}₽`);
      return;
    }

    setUsdtAmount(usdtNum);
    setWalletAddress(wallet);
    setIsLoading(true);

    try {
      const response = await orderApi.createUsdtOrder(
        0,
        usdtNum,
        wallet,
        network,
        paymentMethod,
        authUser?.email || undefined,
        price?.final_price,
        assetCode
      );

      navigate(`/payment/${response.data.uuid}`);
    } catch (error: any) {
      console.error('Error creating order:', error);
      const errorMessage = error?.response?.data?.detail || 'Ошибка создания заказа. Попробуйте позже.';
      showToast('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getText = (key: string, defaultValue: string) => settings[key] || defaultValue;

  const getPaymentIcon = (method?: PaymentMethod) => {
    if (!method) return null;
    const src = method.icon_url || (method.code === 'sbp' ? '/sbp.svg' : '');
    return src ? <img src={src} alt={method.name} className="payment-method-icon" /> : null;
  };

  const finalPrice = price?.final_price || 0;
  const isAuthed = Boolean(authToken && authUser);
  const walletLooksValid = looksLikeAddress(wallet, network);
  const isFormValid = Boolean(
    walletLooksValid &&
    paymentMethod &&
    parseFloat(usdtInput) > 0
  );

  // Get rate for display
  const currentRate = selectedAsset?.base_price || 97;
  const commissionPct = parseFloat(settings['service_commission_percent'] || '10');
  const minAsset = selectedAsset?.min_amount || 1;
  const maxAsset = selectedAsset?.max_amount || 10000;

  // Продажа доступна, когда бэкенд отдал курс (Rapira минус %) и Velox настроен
  const sellEnabled = Boolean(sell?.enabled && sell.rate);
  const isSell = direction === 'sell' && sellEnabled;
  const sellRate = sell?.rate || 0;
  const sellMin = sell?.min_usdt || 10;
  const sellMax = sell?.max_usdt || 5000;
  const sellUsdtNum = parseFloat(sellUsdt) || 0;
  // Выплата в целых рублях, округление вниз — так же считает бэкенд
  const sellRubValue = sellRate > 0 && sellUsdtNum > 0 ? Math.floor(sellUsdtNum * sellRate) : 0;
  const sellRubDisplay = sellInputMode === 'rub' ? sellRubInput : (sellRubValue ? String(sellRubValue) : '');
  const cardNumber = cardDigits(card);
  const cardValid = luhnOk(cardNumber);
  const cardSchemeName = cardScheme(cardNumber);
  // Ошибку в номере показываем, как только цифр набралось на карту (16),
  // или после ухода из поля — не на каждой нажатой цифре.
  const cardError = Boolean(card) && !cardValid && (cardTouched || cardNumber.length >= 16);
  const isSellValid = cardValid && sellUsdtNum > 0;

  const cardBin = cardNumber.slice(0, 6);
  const [bankByBin, setBankByBin] = useState<{ bin: string; bank: string | null } | null>(null);
  useEffect(() => {
    if (cardBin.length < 6) return;
    let alive = true;
    cardBank(cardBin).then((bank) => {
      if (alive) setBankByBin({ bin: cardBin, bank });
    });
    return () => {
      alive = false;
    };
  }, [cardBin]);
  const cardBankName = bankByBin && bankByBin.bin === cardBin ? bankByBin.bank : null;
  const bankLookupDone = cardBin.length === 6 && bankByBin?.bin === cardBin;

  const onSellUsdtChange = (value: string) => {
    setSellInputMode('usdt');
    setSellUsdt(value.replace(',', '.').replace(/[^0-9.]/g, ''));
  };

  const onSellRubChange = (value: string) => {
    const raw = value.replace(',', '.').replace(/[^0-9.]/g, '');
    setSellInputMode('rub');
    setSellRubInput(raw);
    const rub = parseFloat(raw);
    // Сколько USDT нужно отдать, чтобы получить не меньше введённых рублей
    setSellUsdt(rub > 0 && sellRate > 0 ? String(Math.ceil((rub / sellRate) * 100) / 100) : '');
  };

  const switchDirection = (next: 'buy' | 'sell') => {
    setDirection(next);
    setIsSendOpen(false);
  };

  const handleSell = async () => {
    if (isLoading) return;
    if (!authToken || !authUser) {
      showToast('info', 'Войдите в аккаунт, чтобы продать USDT');
      navigate('/login', { state: { from: '/' } });
      return;
    }
    if (!authUser.is_fully_verified) {
      showToast('error', 'Подтвердите телефон в личном кабинете');
      navigate('/account');
      return;
    }
    if (!authUser.full_name || authUser.full_name.trim().length < 2) {
      showToast('error', 'Укажите ФИО в разделе верификации');
      navigate('/account/verification');
      return;
    }
    if (sellUsdtNum < sellMin) {
      showToast('error', `Минимальная продажа: ${sellMin} USDT`);
      return;
    }
    if (sellUsdtNum > sellMax) {
      showToast('error', `Максимальная продажа: ${sellMax} USDT`);
      return;
    }
    if (!cardValid) {
      setCardTouched(true);
      showToast('error', 'Проверьте номер карты');
      return;
    }

    setIsLoading(true);
    try {
      const response = await orderApi.createSellOrder(sellUsdtNum, cardNumber, sellRubValue);
      navigate(`/payment/${response.data.uuid}`);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Ошибка создания заказа. Попробуйте позже.';
      showToast('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Курс живой: подтягиваем цены, пока вкладка открыта, и подсвечиваем изменение.
  useEffect(() => {
    const id = window.setInterval(async () => {
      if (document.hidden) return;
      try {
        const { data } = await publicApi.getConfig();
        if (data?.prices) setPrices(data.prices);
        setSell(data?.sell || null);
      } catch {
        // сеть моргнула — покажем прошлый курс, следующий тик попробует снова
      }
      setRateTick((n) => n + 1);
    }, RATE_REFRESH_S * 1000);
    return () => window.clearInterval(id);
  }, [setPrices, setSell]);

  // Выпадашки закрываются кликом мимо
  useEffect(() => {
    if (!isAssetOpen && !isPaymentOpen && !isSendOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (isSendOpen && sendRef.current && !sendRef.current.contains(target)) setIsSendOpen(false);
      if (isAssetOpen && assetRef.current && !assetRef.current.contains(target)) setIsAssetOpen(false);
      if (isPaymentOpen && methodRef.current && !methodRef.current.contains(target)) setIsPaymentOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isAssetOpen, isPaymentOpen, isSendOpen]);

  // Пришли по ссылке /#faq с другой страницы — докручиваем, когда секция отрисована.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const t = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 150);
    return () => window.clearTimeout(t);
  }, []);

  const onRubChange = (value: string) => {
    const raw = value.replace(',', '.').replace(/[^0-9.]/g, '');
    setInputMode('rub');
    setRubInput(raw);
    const numRub = parseFloat(raw);
    if (numRub > 0) {
      const usdt = usdtFromRub(numRub);
      setUsdtInput(usdt > 0 ? usdt.toFixed(4) : '');
    } else {
      setUsdtInput('');
    }
  };

  const onAssetAmountChange = (value: string) => {
    setInputMode('usdt');
    setUsdtInput(value.replace(',', '.').replace(/[^0-9.]/g, ''));
  };

  // Режим переключается вводом, а не фокусом: иначе один клик по полю пересчитал бы сумму.
  // В неактивном поле показываем округлённое значение, в активном — то, что ввёл человек.
  const rubDisplay = inputMode === 'rub'
    ? rubInput
    : rubInput ? String(Math.round(parseFloat(rubInput))) : '';
  const assetDisplay = inputMode === 'usdt'
    ? usdtInput
    : usdtInput ? String(Number(parseFloat(usdtInput).toFixed(Math.max(assetDecimals, 4)))) : '';

  const fmtRate = currentRate.toLocaleString('ru-RU');
  const heroTitle = getText('hero_title', 'Покупайте и продавайте USDT за рубли');
  // Пример для иллюстрации шагов — считается по живому курсу тем же калькулятором
  const exampleRub = 10000;
  const exampleAsset = usdtFromRub(exampleRub).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const supportLink = settings['support_link'] || '';
  const hasManyMethods = paymentMethods.length > 1;

  return (
    <div className="rx-page">
      <Header />

      <main className="rx-main">
        {/* Первый экран: заголовок, факты и виджет — как у обменников-референсов */}
        <section className="rx-hero">
          <div className="rx-container">
            {/* Неразрывный пробел перед тире — чтобы строка не начиналась с «—» */}
            <h1 className="rx-hero__title">
              {heroTitle.replace(/ — /g, '\u00a0— ').split(/(USDT)/).map((part, i) =>
                part === 'USDT' ? <span key={i} className="rx-hero__accent">{part}</span> : part
              )}
            </h1>
            <p className="rx-hero__subtitle">
              {getText('hero_subtitle', 'Покупка по СБП, продажа с выплатой на карту. Сеть TRC-20.')}
            </p>

            <div className="rx-card" id="exchange">
              {/* Отдаёте: RUB (покупка) или USDT (продажа) — как у Neopass */}
              <div className="rx-panel" ref={sendRef}>
                <div className="rx-panel__top">
                  <span>Отдаёте</span>
                  {isSell ? (
                    <span className="rx-panel__hint">Мин: <b>{sellMin.toLocaleString('ru-RU')} USDT</b></span>
                  ) : selectedMethodObj?.min_amount ? (
                    <span className="rx-panel__hint">
                      Мин: <b>{selectedMethodObj.min_amount.toLocaleString('ru-RU')} ₽</b>
                    </span>
                  ) : null}
                </div>
                <div className="rx-panel__row">
                  {sellEnabled ? (
                    <button
                      type="button"
                      className={'rx-coin rx-coin--btn' + (isSendOpen ? ' rx-coin--open' : '')}
                      onClick={() => setIsSendOpen(!isSendOpen)}
                      aria-haspopup="listbox"
                      aria-expanded={isSendOpen}
                    >
                      {isSell ? <UsdtFace icon={assetIcon} /> : <RubFace sub={selectedMethodObj?.name || 'СБП'} />}
                      <svg className="rx-coin__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                  ) : (
                    <div className="rx-coin"><RubFace sub={selectedMethodObj?.name || 'СБП'} /></div>
                  )}
                  <input
                    type="text"
                    inputMode="decimal"
                    className="rx-amount"
                    placeholder="0"
                    aria-label={isSell ? 'Сколько USDT отдаёте' : 'Сумма в рублях'}
                    value={isSell ? sellUsdt : rubDisplay}
                    onChange={(e) => (isSell ? onSellUsdtChange(e.target.value) : onRubChange(e.target.value))}
                  />
                </div>

                {isSendOpen && (
                  <div className="rx-dropdown" role="listbox">
                    <button
                      type="button"
                      role="option"
                      aria-selected={!isSell}
                      className={'rx-option' + (!isSell ? ' rx-option--active' : '')}
                      onClick={() => switchDirection('buy')}
                    >
                      <span className="rx-coin__icon rx-coin__icon--rub rx-option__tile">₽</span>
                      <span>
                        <span className="rx-coin__name" style={{ fontSize: 16 }}>RUB</span>
                        <span className="rx-coin__sub">Покупка USDT, оплата по СБП</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSell}
                      className={'rx-option' + (isSell ? ' rx-option--active' : '')}
                      onClick={() => switchDirection('sell')}
                    >
                      <img src={assetIcon} alt="" />
                      <span>
                        <span className="rx-coin__name" style={{ fontSize: 16 }}>
                          USDT <span className="rx-chip-net">TRC-20</span>
                        </span>
                        <span className="rx-coin__sub">Продажа USDT, рубли на карту</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>

              <div className="rx-swap" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M6 13l6 6 6-6" />
                </svg>
              </div>

              {/* Получаете: крипта (покупка) или рубли на карту (продажа) */}
              {isSell ? (
                <div className="rx-panel">
                  <div className="rx-panel__top">
                    <span>Получаете</span>
                  </div>
                  <div className="rx-panel__row">
                    <div className="rx-coin">
                      <span className="rx-coin__icon rx-coin__icon--card" aria-hidden="true"><IconCard /></span>
                      <span>
                        <span className="rx-coin__name">RUB</span>
                        <span className="rx-coin__sub">На банковскую карту</span>
                      </span>
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="rx-amount"
                      placeholder="≈ 0"
                      aria-label="Сколько рублей получите"
                      value={sellRubDisplay}
                      onChange={(e) => onSellRubChange(e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <div className="rx-panel" ref={assetRef}>
                  <div className="rx-panel__top">
                    <span>Получаете</span>
                    <span className="rx-panel__hint">
                      <b>{minAsset}–{maxAsset.toLocaleString('ru-RU')}</b> {ticker}
                    </span>
                  </div>
                  <div className="rx-panel__row">
                    {assets.length > 1 ? (
                      <button
                        type="button"
                        className={'rx-coin rx-coin--btn' + (isAssetOpen ? ' rx-coin--open' : '')}
                        onClick={() => setIsAssetOpen(!isAssetOpen)}
                        aria-haspopup="listbox"
                        aria-expanded={isAssetOpen}
                      >
                        <CoinFace icon={assetIcon} ticker={ticker} network={network} name={assetName || selectedAsset?.label || ticker} />
                        <svg className="rx-coin__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    ) : (
                      <div className="rx-coin">
                        <CoinFace icon={assetIcon} ticker={ticker} network={network} name={assetName || 'Tether'} />
                      </div>
                    )}
                    <input
                      type="text"
                      inputMode="decimal"
                      className="rx-amount"
                      placeholder="0"
                      aria-label={`Количество ${ticker}`}
                      value={assetDisplay}
                      onChange={(e) => onAssetAmountChange(e.target.value)}
                    />
                  </div>

                  {isAssetOpen && (
                    <div className="rx-dropdown" role="listbox">
                      {assets.map((item) => (
                        <button
                          key={item.code}
                          type="button"
                          role="option"
                          aria-selected={item.code === assetCode}
                          className={'rx-option' + (item.code === assetCode ? ' rx-option--active' : '')}
                          onClick={() => {
                            if (item.code !== assetCode) {
                              setAsset(item.code);
                              // Адрес и суммы принадлежали прошлой монете — сбрасываем.
                              setWallet('');
                              setUsdtInput('');
                              setRubInput('');
                            }
                            setIsAssetOpen(false);
                          }}
                        >
                          <img src={item.icon_url || '/usdt.svg'} alt="" />
                          <span>
                            <span className="rx-coin__name" style={{ fontSize: 16 }}>
                              {item.ticker} <span className="rx-chip-net">{item.network}</span>
                            </span>
                            {item.label !== item.ticker && <span className="rx-coin__sub">{item.label}</span>}
                          </span>
                          <span className="rx-option__price">{item.base_price.toLocaleString('ru-RU')} ₽</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="rx-rate">
                <span className="rx-rate__label">
                  Курс
                  <svg className="rx-ring" viewBox="0 0 18 18" aria-label={`Обновляется каждые ${RATE_REFRESH_S} секунд`}>
                    <circle className="rx-ring__track" cx="9" cy="9" r="7" fill="none" strokeWidth="2.5" />
                    <circle
                      key={rateTick}
                      className="rx-ring__bar"
                      cx="9" cy="9" r="7" fill="none" strokeWidth="2.5" strokeLinecap="round"
                      style={{ ['--rx-period' as string]: `${RATE_REFRESH_S}s` }}
                    />
                  </svg>
                </span>
                <span className="rx-rate__value">
                  {isSell
                    ? `1 USDT = ${sellRate.toLocaleString('ru-RU')} ₽`
                    : `1 ${ticker} = ${fmtRate} ₽`}
                </span>
              </div>

              <div className="rx-fields">
                {isSell ? (
                  <div>
                  <div className={'input-wrapper' + (cardError ? ' input-wrapper--invalid' : cardValid ? ' input-wrapper--valid' : '')}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      className="input"
                      placeholder="Номер карты для выплаты"
                      value={card}
                      onChange={(e) => setCard(formatCard(e.target.value))}
                      onBlur={() => setCardTouched(true)}
                    />
                    <span className="input-icon"><IconCard /></span>
                  </div>
                  {card && (cardSchemeName || cardBin.length === 6) && (
                    <div className={'rx-card-hint' + (cardError ? ' rx-card-hint--error' : cardValid ? ' rx-card-hint--ok' : '')} aria-live="polite">
                      <span className="rx-card-hint__bank">
                        {cardBankName
                          ? cardBankName
                          : bankLookupDone
                            ? 'Банк не определён'
                            : cardBin.length === 6
                              ? 'Определяем банк…'
                              : null}
                        {cardSchemeName && (
                          <span className="rx-card-hint__scheme">
                            {cardBankName || cardBin.length === 6 ? ' · ' : ''}{cardSchemeName}
                          </span>
                        )}
                      </span>
                      {cardError ? (
                        <span>В номере ошибка — проверьте цифры</span>
                      ) : cardValid ? (
                        <span>✓ Номер корректный</span>
                      ) : null}
                    </div>
                  )}
                  </div>
                ) : (
                  <>
                    <WalletSelector
                      value={wallet}
                      onChange={setWallet}
                      enabled={isAuthed}
                      network={network}
                    />

                    <div className="rx-method" ref={methodRef}>
                      {hasManyMethods ? (
                        <button type="button" className="rx-method__control" onClick={() => setIsPaymentOpen(!isPaymentOpen)}>
                          {selectedMethodObj ? (
                            <>
                              {getPaymentIcon(selectedMethodObj)}
                              {selectedMethodObj.name}
                            </>
                          ) : (
                            <span className="rx-method__label">Выберите способ оплаты</span>
                          )}
                          <svg style={{ marginLeft: 'auto' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                      ) : (
                        <div className="rx-method__control">
                          <span className="rx-method__tile">
                            {selectedMethodObj?.code === 'sbp' || !selectedMethodObj
                              ? <img src="/sbp-mark.svg" alt="" />
                              : getPaymentIcon(selectedMethodObj)}
                          </span>
                          <span className="rx-method__text">
                            <span className="rx-method__name">{selectedMethodObj?.name || 'СБП'}</span>
                            <span className="rx-method__sub">{selectedMethodObj?.description || 'Система быстрых платежей'}</span>
                          </span>
                          {selectedMethodObj?.commission_text && (
                            <span className="rx-method__fee">Комиссия {selectedMethodObj.commission_text}</span>
                          )}
                        </div>
                      )}

                      {isPaymentOpen && (
                        <div className="rx-dropdown" role="listbox">
                          {paymentMethods.map((method) => (
                            <button
                              key={method.code}
                              type="button"
                              role="option"
                              aria-selected={paymentMethod === method.code}
                              className={'rx-option' + (paymentMethod === method.code ? ' rx-option--active' : '')}
                              onClick={() => {
                                setPaymentMethod(method.code);
                                setIsPaymentOpen(false);
                              }}
                            >
                              {getPaymentIcon(method)}
                              <span>{method.name}</span>
                              {method.commission_text && (
                                <span className="rx-option__price">{method.commission_text}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {price && parseFloat(usdtInput) > 0 && (price.discount > 0 || commissionsEnabled) && (
                      <div className="rx-summary">
                        {price.discount > 0 && (
                          <div className="rx-summary__row rx-summary__row--minus">
                            <span>Скидка за объём</span>
                            <span>−{price.discount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</span>
                          </div>
                        )}
                        {commissionsEnabled && (
                          <>
                            <div className="rx-summary__row">
                              <span>{getText('commission_text', 'Комиссия сервиса')} ({commissionPct}%)</span>
                              <span>+{price.service_commission.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</span>
                            </div>
                            <div className="rx-summary__row">
                              <span>Комиссия за перевод (+{transferFeeUsdtGlobal} {ticker})</span>
                              <span>+{price.transfer_fee.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</span>
                            </div>
                          </>
                        )}
                        <div className="rx-summary__row rx-summary__row--total">
                          <span>Итого</span>
                          <span>{price.final_price.toLocaleString('ru-RU')} ₽</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <button
                type="button"
                className={'rx-cta' + ((isSell ? isSellValid : isFormValid) || !isAuthed ? '' : ' rx-cta--muted')}
                disabled={isLoading}
                onClick={isSell ? handleSell : handlePay}
              >
                {isLoading
                  ? 'Создаём заказ…'
                  : !isAuthed
                    ? 'Войти, чтобы продолжить'
                    : !authUser?.is_fully_verified
                      ? 'Подтвердить телефон'
                      : isSell
                        ? (parseFloat(sellUsdt) > 0 ? `Продать ${sellUsdt} USDT` : 'Продать USDT')
                        : finalPrice > 0
                          ? `Оплатить ${finalPrice.toLocaleString('ru-RU')} ₽`
                          : 'Оплатить'}
              </button>

              <p className="rx-terms">
                Продолжая, вы соглашаетесь с{' '}
                <a href={settings['terms_link'] || '/terms'}>Пользовательским соглашением</a>
              </p>
            </div>

            <ul className="rx-trust" aria-label="Платёжные системы и сети">
              <li><img src="/sbp.svg" alt="СБП" className="rx-trust__sbp" /></li>
              <li><img src={assetIcon} alt="" /> Tether {ticker}</li>
              <li><img src="/tron.svg" alt="" /> TRON {network}</li>
            </ul>
          </div>
        </section>

        {/* Как это работает */}
        <section className="rx-section rx-section--band" id="how">
          <div className="rx-container">
            <h2 className="rx-h2">Как это работает</h2>
            <p className="rx-section__lead">Три шага и пара минут — без бирж, P2P и поиска продавца.</p>
            <ol className="rx-steps">
              <li className="rx-step">
                <div className="rx-step__art" aria-hidden="true">
                  <div className="rx-mini">
                    <div className="rx-mini__row">
                      <span className="rx-mini__label">Отдаёте</span>
                      <span className="rx-mini__value">{exampleRub.toLocaleString('ru-RU')} ₽</span>
                    </div>
                    <div className="rx-mini__row">
                      <span className="rx-mini__label">Получаете</span>
                      <span className="rx-mini__value rx-mini__value--accent">
                        {exampleAsset} {ticker}
                      </span>
                    </div>
                  </div>
                </div>
                <span className="rx-step__num">Шаг 1</span>
                <h3 className="rx-step__title">Укажите сумму и кошелёк</h3>
                <p className="rx-step__text">Введите сумму в рублях или {ticker} и адрес кошелька в сети {network}.</p>
              </li>
              <li className="rx-step">
                <div className="rx-step__art" aria-hidden="true">
                  <div className="rx-mini rx-mini--qr">
                    <QRCodeSVG value="https://rubex.cc" size={84} bgColor="transparent" fgColor="currentColor" />
                    <img src="/sbp.svg" alt="" className="rx-mini__sbp" />
                  </div>
                </div>
                <span className="rx-step__num">Шаг 2</span>
                <h3 className="rx-step__title">Оплатите по СБП</h3>
                <p className="rx-step__text">Отсканируйте QR-код в приложении банка — без реквизитов и ручного ввода.</p>
              </li>
              <li className="rx-step">
                <div className="rx-step__art" aria-hidden="true">
                  <div className="rx-mini rx-mini--notify">
                    <img src={assetIcon} alt="" className="rx-mini__coin" />
                    <span>
                      <span className="rx-mini__value">+{exampleAsset} {ticker}</span>
                      <span className="rx-mini__label">Зачислено · {network}</span>
                    </span>
                    <span className="rx-mini__check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                  </div>
                </div>
                <span className="rx-step__num">Шаг 3</span>
                <h3 className="rx-step__title">Получите {ticker}</h3>
                <p className="rx-step__text">Отправляем на ваш кошелёк за 5–15 минут после подтверждения оплаты.</p>
              </li>
            </ol>
          </div>
        </section>

        {/* Преимущества — плоский тёмный блок */}
        <section className="rx-section">
          <div className="rx-container">
            <div className="rx-dark">
              <div className="rx-dark__lead">
                <h2 className="rx-dark__title">Рубли в {ticker} за 5–15 минут</h2>
                <p className="rx-dark__text">
                  Сумма к оплате фиксируется при создании заказа — вы видите её на кнопке ещё до перевода.
                </p>
                <dl className="rx-dark__stats">
                  <div><dt>{fmtRate} ₽</dt><dd>за 1 {ticker} сейчас</dd></div>
                  <div><dt>от {minAsset} {ticker}</dt><dd>минимальная покупка</dd></div>
                </dl>
              </div>
              <div className="rx-features">
                <div className="rx-feature">
                  <span className="rx-feature__icon"><IconBolt /></span>
                  <div>
                    <h3 className="rx-feature__title">{getText('benefit_1_title', 'Быстро')}</h3>
                    <p className="rx-feature__text">{getText('benefit_1_text', 'USDT отправляется в течение 5-15 минут после оплаты')}</p>
                  </div>
                </div>
                <div className="rx-feature">
                  <span className="rx-feature__icon"><IconShield /></span>
                  <div>
                    <h3 className="rx-feature__title">{getText('benefit_2_title', 'Надёжно')}</h3>
                    <p className="rx-feature__text">{getText('benefit_2_text', 'Безопасные платежи через СБП')}</p>
                  </div>
                </div>
                <div className="rx-feature">
                  <span className="rx-feature__icon"><IconChat /></span>
                  <div>
                    <h3 className="rx-feature__title">{getText('benefit_3_title', 'Поддержка 24/7')}</h3>
                    <p className="rx-feature__text">{getText('benefit_3_text', 'Мы всегда на связи в Telegram')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="rx-section" id="faq">
          <div className="rx-container rx-faq">
            <div>
              <h2 className="rx-h2">Частые вопросы</h2>
              {supportLink && (
                <p className="rx-faq__help">
                  Не нашли ответ?{' '}
                  <a href={supportLink} target="_blank" rel="noopener noreferrer">Напишите в поддержку</a>
                </p>
              )}
            </div>
            <div className="rx-faq__list">
              {faqItems.map((item, index) => (
                <div key={index} className={'rx-faq__item' + (openFaq === index ? ' rx-faq__item--open' : '')}>
                  <button
                    type="button"
                    className="rx-faq__q"
                    aria-expanded={openFaq === index}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{item.q}</span>
                    <svg className="rx-faq__chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  <div className="rx-faq__a-wrap">
                    <div className="rx-faq__a-inner">
                      <div className="rx-faq__a">{item.a}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rx-section">
          <div className="rx-container">
            <div className="rx-final">
              <div>
                <h2 className="rx-final__title">Купите первые {ticker} сегодня</h2>
                <p className="rx-final__text">1 {ticker} = {fmtRate} ₽ · оплата по СБП из приложения вашего банка</p>
              </div>
              <a href="#exchange" className="rx-btn rx-btn--dark">Купить {ticker}</a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <Toast messages={toasts} onRemove={removeToast} />
    </div>
  );
}

const RATE_REFRESH_S = 60;

function CoinFace({ icon, ticker, network, name }: { icon: string; ticker: string; network: string; name: string }) {
  return (
    <>
      <span className="rx-coin__icon"><img src={icon} alt="" /></span>
      <span>
        <span className="rx-coin__name">
          {ticker} <span className="rx-chip-net">{network}</span>
        </span>
        <span className="rx-coin__sub">{name}</span>
      </span>
    </>
  );
}

const iconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function IconBolt() {
  return <svg {...iconProps}><path d="M13 2L4.1 12.7a.5.5 0 0 0 .4.8H11l-1 8.5 8.9-10.7a.5.5 0 0 0-.4-.8H12l1-8.5z" /></svg>;
}

function IconShield() {
  return <svg {...iconProps}><path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>;
}

function IconChat() {
  return <svg {...iconProps}><path d="M21 11.5a8.4 8.4 0 0 1-12.3 7.4L3 21l2.1-5.7A8.4 8.4 0 1 1 21 11.5z" /><path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" /></svg>;
}

function IconCard() {
  return <svg {...iconProps} width={20} height={20}><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19M6.5 15h4" /></svg>;
}

function RubFace({ sub }: { sub: string }) {
  return (
    <>
      <span className="rx-coin__icon rx-coin__icon--rub">₽</span>
      <span>
        <span className="rx-coin__name">RUB</span>
        <span className="rx-coin__sub">{sub}</span>
      </span>
    </>
  );
}

function UsdtFace({ icon }: { icon: string }) {
  return <CoinFace icon={icon} ticker="USDT" network="TRC-20" name="Tether" />;
}
